import { Application, Rectangle, Ticker, utils } from "pixi.js";
import screenfull from "screenfull";
import { ResizerMode } from "./ResizerMode";
import { ResizerOptions } from "./ResizerOptions";
import { ResizerScaleType } from "./ResizerScaleType";

/**
 */
export class Resizer extends utils.EventEmitter {
  /**
   */
  static ticker: Ticker = new Ticker();

  /**
   * Pixel density multiplier.
   */
  public scaleFactor: number;

  /**
   * Actual width before scaling.
   */
  public width: number;

  /**
   * Actual height before scaling.
   */
  public height: number;

  /**
   * Max width for flexible aspect ratio.
   */
  public minWidth: number;

  /**
   * Max width for flexible aspect ratio.
   */
  public maxWidth: number;

  /**
   * The type of scaling if any applied.
   */
  public scaleType: ResizerScaleType;

  /**
   * Resizer mode
   */
  public resizerMode: ResizerMode;

  /**
   * If set true, a resize update will be forced on next update.
   */
  public dirty: boolean = false;

  /**
   */
  public maxPixelRatio: number = 2;

  /**
   * Stores values for change detection.
   */
  private currentWidth: number = 0;

  /**
   * Stores values for change detection.
   */
  private currentHeight: number = 0;

  /**
   * Stores values for change detection.
   */
  private currentPixelRatio: number = 0;

  /**
   * The size and position of the app represented as a rectangle
   * within the dom window or container.
   */
  private viewRect: Rectangle = new Rectangle();

  /**
   * Reference to the PIXI.Application instance being resized.
   */
  private app: Application;

  private resizeTimeout: number;

  /**
   */
  constructor(app: Application, options?: ResizerOptions) {
    super();
    this.app = app;
    this.parseOptions(options);
    this.setCanvasElementStyle(app.view);
    Resizer.ticker.start();
    Resizer.ticker.add(() => this.update());
    window.addEventListener("resize", () => {
      this.dirty = true;
    });
  }

  /**
   * Set style properties on dom canvas element.
   */
  private setCanvasElementStyle(el: HTMLElement) {
    el.style.position = "absolute";
    el.style.webkitTapHighlightColor = "transparent";
  }

  /**
   * Parse ResizerOptions object.
   */
  private parseOptions(options?: ResizerOptions) {
    options = options || ({} as ResizerOptions);
    this.resizerMode = options.resizerMode || ResizerMode.None;
    this.scaleType = options.scaleType || ResizerScaleType.None;
    this.scaleFactor = options.scaleFactor || 1;
    this.width = options.width || 1280;
    this.height = options.height || 720;
    this.minWidth = options.minWidth || this.width;
    this.maxWidth = options.maxWidth || this.width;
    this.maxPixelRatio = options.maxPixelRatio || 2;
  }

  /**
   * Poll container for updates.
   */
  public update(instant?: boolean) {
    if (this.detectChange()) {
      if (this.resizeTimeout) {
        window.clearTimeout(this.resizeTimeout);
        this.resizeTimeout = null;
      }
      if (instant) {
        this.resize();
        this.emit("resize", this.width, this.height);
      } else {
        this.resizeTimeout = window.setTimeout(() => {
          this.resize();
          this.emit("resize", this.width, this.height);
        }, 1);
      }
    }
  }

  /**
   * Detect if update is needed.
   */
  private detectChange(): boolean {
    let changed = false;
    if (
      this.dirty ||
      window.innerWidth !== this.currentWidth ||
      window.innerHeight !== this.currentHeight ||
      window.devicePixelRatio !== this.currentPixelRatio
    ) {
      changed = true;
      this.currentWidth = window.innerWidth;
      this.currentHeight = window.innerHeight;
      this.currentPixelRatio = window.devicePixelRatio;
    }
    if (this.dirty) this.dirty = false;
    return changed;
  }

  /**
   * Apply resizing.
   */
  private resize() {
    switch (this.resizerMode) {
      case ResizerMode.Fill:
        this.fill();
        this.setStyle();
        break;

      case ResizerMode.Aspect:
        this.fillAspect();
        this.setStyle();
        break;

      case ResizerMode.None:
      default:
        this.app.renderer.resize(this.width, this.height);
        break;
    }
  }

  /**
   * Fill container.
   */
  private fill(): void {
    this.viewRect.x = 0;
    this.viewRect.y = 0;
    this.viewRect.width = window.innerWidth;
    this.viewRect.height = window.innerHeight;
  }

  /**
   * Fill container with fixed aspect ratio.
   */
  private fillAspect(): void {
    const r = window.innerWidth / window.innerHeight;
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (r < this.minWidth / this.height) {
      // If window aspect ratio is less than min aspect ratio.
      this.viewRect.width = Math.ceil(w);
      this.viewRect.height = Math.ceil(w * (this.height / this.minWidth));
      this.viewRect.x = 0;
      this.viewRect.y = Math.ceil((h - this.viewRect.height) / 2);
      this.width = this.minWidth;
    } else if (r > this.maxWidth / this.height) {
      // If window aspect ratio is less than max aspect ratio.
      this.viewRect.width = Math.ceil(h * (this.maxWidth / this.height));
      this.viewRect.height = Math.ceil(h);
      this.viewRect.x = Math.ceil((w - this.viewRect.width) / 2);
      this.viewRect.y = 0;
      this.width = this.maxWidth;
    } else {
      // If window aspect ratio is between min and max aspect ratios.
      this.viewRect.width = Math.ceil(w);
      this.viewRect.height = Math.ceil(h);
      this.viewRect.x = 0;
      this.viewRect.y = 0;
      this.width = Math.ceil(this.height * (this.viewRect.width / this.viewRect.height));
    }
  }

  /**
   * Set dom element properties.
   */
  private setStyle(): void {
    switch (this.scaleType) {
      case ResizerScaleType.CSS:
        this.app.view.style.left = this.viewRect.x + "px";
        this.app.view.style.top = this.viewRect.y + "px";
        this.app.view.style.width = this.viewRect.width + "px";
        this.app.view.style.height = this.viewRect.height + "px";
        this.app.renderer.resize(this.width, this.height);
        break;

      case ResizerScaleType.Stage:
        const dpr = Math.min(this.maxPixelRatio, window.devicePixelRatio);
        this.app.renderer.resize(
          Math.min(this.viewRect.width * dpr, this.width * this.scaleFactor),
          Math.min(this.viewRect.height * dpr, this.height * this.scaleFactor)
        );
        const resolution = this.app.renderer.resolution;
        this.app.stage.scale.set(this.app.view.height / this.height / resolution);
        this.app.view.style.left = this.viewRect.x + "px";
        this.app.view.style.top = this.viewRect.y + "px";
        this.app.view.style.width = this.viewRect.width + "px";
        this.app.view.style.height = this.viewRect.height + "px";
        break;

      case ResizerScaleType.None:
      default:
        this.app.renderer.resize(this.viewRect.width, this.viewRect.height);
        break;
    }
  }
}
